import React from 'react';
import { Timer, Heart, FootprintsIcon, TrendingUp, Wind, Droplets } from 'lucide-react';
import { motion } from 'framer-motion';

const StatCard = ({ icon: Icon, value, label, trend, color = "white" }) => (
  <motion.div 
    className="glass p-4 rounded-lg"
    whileHover={{ scale: 1.02 }}
    transition={{ type: "spring", stiffness: 400, damping: 17 }}
  >
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-4 h-4 text-${color}`} />
      <span className="text-sm text-white/70">{label}</span>
    </div>
    <div className="flex items-baseline gap-2">
      <div className="text-2xl font-bold">{value}</div>
      {trend && (
        <TrendingUp className={`w-4 h-4 ${
          trend === 'up' ? 'text-green-400 rotate-0' : 'text-red-400 rotate-180'
        }`} />
      )}
    </div>
  </motion.div>
);

const ZoneIndicator = ({ value, zones }) => {
  const getZone = () => {
    for (const zone of zones) {
      if (value >= zone.min && value <= zone.max) {
        return zone;
      }
    }
    return zones[0];
  };

  const currentZone = getZone();

  return (
    <div className="mt-2">
      <div className="flex justify-between text-xs text-white/50 mb-1">
        <span>Recovery</span>
        <span>Aerobic</span>
        <span>Threshold</span>
        <span>VO2 Max</span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden flex">
        {zones.map((zone, index) => (
          <div
            key={index}
            className={`flex-1 ${zone.color} ${
              currentZone.name === zone.name ? 'opacity-100' : 'opacity-30'
            }`}
          />
        ))}
      </div>
      <div className="text-xs text-white/70 mt-1">
        Currently in <span className="font-medium text-white">{currentZone.name}</span> zone
      </div>
    </div>
  );
};

export const RunningStats = () => {
  const heartRateZones = [
    { name: 'Recovery', min: 0, max: 140, color: 'bg-blue-400' },
    { name: 'Aerobic', min: 141, max: 160, color: 'bg-green-400' },
    { name: 'Threshold', min: 161, max: 175, color: 'bg-yellow-400' },
    { name: 'VO2 Max', min: 176, max: 200, color: 'bg-red-400' }
  ];

  return (
    <motion.div 
      className="glass p-6 rounded-xl space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="grid grid-cols-3 gap-4">
        <StatCard
          icon={Timer}
          value="4:50"
          label="Pace (/km)"
          trend="up"
          color="green-400"
        />
        <StatCard
          icon={Heart}
          value="154"
          label="Heart Rate"
          color="red-400"
        />
        <StatCard
          icon={FootprintsIcon}
          value="2.4"
          label="Distance (km)"
          color="blue-400"
        />
      </div>

      <div className="space-y-4">
        <div className="glass p-4 rounded-lg">
          <h3 className="text-sm font-medium mb-2">Heart Rate Zones</h3>
          <ZoneIndicator value={154} zones={heartRateZones} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="glass p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Wind className="w-4 h-4 text-white/70" />
              <span className="text-sm font-medium">Cadence</span>
            </div>
            <div className="text-2xl font-bold">172</div>
            <div className="text-xs text-white/70">steps/min</div>
          </div>

          <div className="glass p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Droplets className="w-4 h-4 text-white/70" />
              <span className="text-sm font-medium">Effort Level</span>
            </div>
            <div className="text-2xl font-bold">7.2</div>
            <div className="text-xs text-white/70">moderate</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};